import { Component, Input, OnChanges, OnInit } from '@angular/core';
import { ChartDataset, ChartType } from 'chart.js';
import { BaseChartDirective } from 'ng2-charts';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { StandardStatisticsData } from '../../../models/statistics';

export interface SummaryData {
  key: string;
  elements: StandardStatisticsData[];
  orderCount: number;
  value: number;
}

type SortKey = 'productId' | 'key' | 'value' | 'count' | 'orderCount';

@Component({
    selector: 'lib-order-analytics-chart-table',
    templateUrl: './order-analytics-chart-table.component.html',
    styleUrls: ['./order-analytics-chart-table.component.css'],
    standalone: false
})
export class OrderAnalyticsChartTableComponent implements OnInit, OnChanges {
  @Input()
  data: StandardStatisticsData[];

  @Input()
  groupBy: string;

  @Input()
  tableOnly: boolean;

  @Input()
  heading: string;

  barChartData: ChartDataset[];
  barChartLabels: BaseChartDirective['labels'] = [];
  barChartOptions: any = {};
  barChartLegend = false;
  barChartType: ChartType = 'bar';

  summaryData: SummaryData[];
  summaryTotals;
  filteredData: SummaryData[] = [];
  sortedData: SummaryData[] = [];
  sortKey: SortKey = 'value';
  sortAsc = false;

  searchData: Subject<Event> = new Subject();
  loading = true;

  ngOnInit() {
    this.barChartOptions = {
      backgroundColor: 'rgba(206,212,218, 0.6)',
      hoverBackgroundColor: 'rgba(206,212,218, 0.8)'
    };
    this.searchData.pipe(debounceTime(100)).subscribe(search => {
      const element = search.target as HTMLInputElement;
      const searchTerm = element.value;
      this.filteredData = this.summaryData.filter(item => {
        if (!!item.elements[0].productModelId) {
          return (
            item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.elements[0].productModelId.includes(searchTerm)
          );
        } else {
          return (
            item.key.toLowerCase().includes(searchTerm.toLowerCase()) ||
            item.elements[0].productId.includes(searchTerm)
          );
        }
      });
      this.applySort();
    });
  }

  ngOnChanges() {
    const groupedData = this.groupData();
    const groupedDataValue = groupedData.map(element => {
      element.value = Math.round(
        element.elements.reduce((acc: number, curr: StandardStatisticsData) => {
          return acc + curr.orderRowTotalValueCurrency;
        }, 0)
      );
      element.count = Math.round(
        element.elements.reduce((acc: number, curr: StandardStatisticsData) => {
          return acc + curr.orderRowTotalQuantity;
        }, 0)
      );
      return element;
    });

    const chartData = [];
    const chartLabels = [];
    groupedDataValue.forEach(element => {
      chartData.push(element.value);
      chartLabels.push(element.key);
    });
    this.barChartData = [{ data: chartData }];
    this.barChartLabels = chartLabels;

    this.buildSummaryData(this.groupBy === 'productName' ? groupedDataValue : groupedDataValue.reverse());

    this.loading = false;
  }

  toggleSort(key: SortKey): void {
    if (this.sortKey === key) {
      this.sortAsc = !this.sortAsc;
    } else {
      this.sortKey = key;
      this.sortAsc = false;
    }
    this.applySort();
  }

  protected applySort(): void {
    const dir = this.sortAsc ? 1 : -1;
    const key = this.sortKey;
    this.sortedData = [...this.filteredData].sort((a, b) => {
      if (key === 'key') {
        return (a.key || '').localeCompare(b.key || '') * dir;
      }
      if (key === 'productId') {
        const aId = a.elements[0]?.productModelId || a.elements[0]?.productId || '';
        const bId = b.elements[0]?.productModelId || b.elements[0]?.productId || '';
        return aId.localeCompare(bId) * dir;
      }
      return ((a[key] as number) - (b[key] as number)) * dir;
    });
  }

  protected groupData(): { [key: string]: StandardStatisticsData[] | any }[] {
    const result = [];
    const groupedData = this.data.reduce((acc, curr) => {
      if (this.groupBy === 'orderCreationDateYear') {
        const quarterKey = 'orderCreationDateYearQuarter';
        const year = curr[quarterKey].split('-')[0];
        (acc[year] = acc[year] || []).push(curr);
      } else {
        (acc[curr[this.groupBy]] = acc[curr[this.groupBy]] || []).push(curr);
      }
      return acc;
    }, {});
    for (const key of Object.keys(groupedData)) {
      result.push({ key, elements: groupedData[key] });
    }
    result.sort((p1, p2) => p1.key.localeCompare(p2.key));
    return result;
  }

  protected buildSummaryData(grouped: { [key: string]: any }) {
    const summaryTotals = { value: 0, count: 0, orderCount: 0 };

    this.summaryData = grouped.map(element => {
      const orderCount = new Set();
      element.elements.forEach((e: StandardStatisticsData) => {
        orderCount.add(e.orderId);
      });
      element.orderCount = orderCount.size;

      summaryTotals.value += element.value;
      summaryTotals.count += element.count;
      summaryTotals.orderCount += element.orderCount;

      return element;
    });

    this.summaryTotals = summaryTotals;
    this.filteredData = [...this.summaryData];
    this.applySort();
  }
}
